import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-chart>` says about itself.
 *
 * The supported types are a DECISION, and the reasoning belongs where somebody reaching for a
 * fourth one will read it. Each of the three is asked for by name inside this repository: AppHome
 * hand-draws the bar because the library had none, the line is the "number moving over time" the
 * work item was raised for, and StatTile's `visual` slot documents itself as wanting "a sparkline
 * or similar" the library could not supply.
 *
 * Pie, donut and a second series are refused for one shared reason: each needs a categorical
 * colour axis, and there is no such axis in the design system — only the semantic families and the
 * neutrals. Adding one is CAOS-586's decision, not a side effect of building a chart. One series
 * needs no such axis, which is also what makes this readable without colour: with a single series,
 * colour carries no information for anybody to miss.
 */
const docs: ComponentDocs = {
  aliases: ['graph', 'trend', 'sparkline', 'bar chart', 'line chart', 'plot', 'trendline', 'histogram'],

  answers: [
    'show a number moving over time',
    'compare a figure across a handful of categories',
    'put a small trend line inside a tile or a cell',
    'show a breakdown by stage without building a report first',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'points',
      // No attribute: a list cannot ride one, exactly as the stepper's steps and the tab strip's
      // tabs do not. `property` says an ATTRIBUTE also has an accessor behind it, so on an input
      // with no attribute the word states nothing — the absent attribute IS the declaration
      // (CAOS-653, gap 2).
      type: 'json',
      default: [],
      description:
        'The readings, in the order they are drawn. Each is either a plain number or an object with ' +
        '`value` and, optionally, `label` and `display`. `display` is the figure already formatted — ' +
        'this piece rounds, groups and abbreviates nothing, the same way StatTile does not, because ' +
        'currency and locale are known where the data comes from and not here. A reading whose value ' +
        'is not a finite number is dropped rather than drawn as a zero.',
    },
    {
      name: 'type',
      attribute: 'type',
      type: 'text',
      options: ['bar', 'line', 'sparkline'],
      // OPEN, not closed: the piece draws a bar for anything it does not recognise, so declaring a
      // closed set would have the platform refuse a value that in fact draws perfectly well.
      fallback: 'bar',
      default: 'bar',
      description:
        'Which drawing. `bar` is one bar per category, scaled against the largest figure in the set. ' +
        '`line` is a value over time with a dot at each reading. `sparkline` is that line with its ' +
        'caption, axis and dots stripped, sized to sit inside another piece. Anything unrecognised ' +
        'reads as `bar`.',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'What the whole set is. Drawn as a caption above the figures, and used as the heading of the ' +
        'text equivalent a line carries. A sparkline draws no caption — it sits inside a piece that ' +
        'has already said what the figures are — but still uses this to name its text equivalent.',
    },
    {
      name: 'max',
      attribute: 'max',
      type: 'number',
      description:
        'The top of the scale. Left out, the largest figure in the set is the top, which makes each ' +
        'chart tell its own story well and makes two charts side by side incomparable. Set it when ' +
        'the point is the comparison — a quota, a target, or the same ceiling across several charts.',
    },
    {
      name: 'emptyLabel',
      attribute: 'empty-label',
      type: 'text',
      default: 'Nothing to show yet.',
      description:
        'What is said when there are no readings. There is always something rather than an empty ' +
        'frame, because a chart with no bars in it reads as "everything is nought", which is a ' +
        'different and usually wrong statement.',
    },
  ],

  events: [],

  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The face of every label, figure and caption.' },
    { token: '--caos-space-2', controls: 'The gap between the caption and the figures, and between one bar and the next.' },
    { token: '--caos-space-3', controls: 'The gap holding a bar’s label apart from its figure.' },
    { token: '--caos-color-text', controls: 'The caption, each bar’s label, and each bar’s figure.' },
    { token: '--caos-color-text-muted', controls: 'The axis at the ends of a line, and the words shown when there is nothing to draw.' },
    { token: '--caos-color-bg', controls: 'The track a bar is drawn along.' },
    { token: '--caos-color-accent', controls: 'The fill of a bar, and the stroke and dots of a line.' },
  ],

  parts: [
    { part: 'chart', is: 'the whole piece' },
    { part: 'caption', is: 'the label above the figures', when: 'a label is set and the type is not a sparkline' },
    { part: 'rows', is: 'the list of bars', when: 'the type is a bar' },
    { part: 'row', is: 'one bar and its heading', when: 'the type is a bar' },
    { part: 'label', is: 'what one bar is', when: 'the type is a bar' },
    { part: 'value', is: 'the figure beside one bar', when: 'the type is a bar' },
    { part: 'track', is: 'the groove a bar is drawn along', when: 'the type is a bar' },
    { part: 'fill', is: 'the drawn length of one bar', when: 'the type is a bar' },
    { part: 'figure', is: 'the drawing itself', when: 'the type is a line or a sparkline' },
    { part: 'line', is: 'the stroke joining the readings', when: 'the type is a line or a sparkline' },
    { part: 'point', is: 'the dot at one reading', when: 'the type is a line' },
    { part: 'axis', is: 'the first and last labels beneath a line', when: 'the type is a line' },
    { part: 'table', is: 'the figures as text, for assistive technology', when: 'the type is a line or a sparkline' },
    { part: 'empty', is: 'what is said when there is nothing to draw', when: 'there are no readings' },
  ],

  methods: [],

  placing:
    'It fills the width it is given and takes its height from the type, so the surrounding region ' +
    'decides how wide a chart is — put one in a Layout column and it sits beside the figures it ' +
    'draws. A sparkline is the exception: it is sized to sit inside another piece rather than on a ' +
    'page of its own, and StatTile’s `visual` slot is where it belongs.',

  whenToUse:
    'When the SHAPE of a set of figures is the point — that it is rising, that one category dwarfs ' +
    'the rest — rather than the figures themselves. A handful of readings up to a few dozen. It ' +
    'takes its data directly and needs no saved report, so a page can draw a trend from data it ' +
    'already has.',

  insteadUse: [
    {
      name: 'StatTile',
      when: 'There is one figure rather than a set, and what matters is the number itself.',
    },
    {
      name: 'ProgressBar',
      when: 'The figure is one proportion of a whole, or how far a long job has got.',
    },
    {
      name: 'DataTable',
      when:
        'The figures need reading exactly, sorting, or comparing against each other row by row. A ' +
        'chart shows a shape; a table answers "what was it in March".',
    },
  ],

  accessibility:
    'Bars are ordinary elements, so each label and figure is already text in the page and is read ' +
    'without help. A line and a sparkline are pictures, so the drawing is hidden from assistive ' +
    'technology and the same figures are carried beside it as a table that is present but not ' +
    'visible — the label names its caption. Nothing here depends on telling two colours apart: ' +
    'there is only ever one series, and every figure sits beside its own label. Two things are left ' +
    'to the author. The piece is not a live region, so figures that change while somebody is ' +
    'looking announce nothing. And a set of readings whose labels do not say what they are — "1", ' +
    '"2", "3" — is as unreadable in the table as it is in the picture, so give each reading a label.',

  examples: [
    {
      key: 'bars',
      label: 'A breakdown by stage',
      summary: 'One bar per category with its own figure beside it, each scaled against the largest in the set.',
      placement: {
        component: 'caos-chart',
        inputs: {
          label: 'Pipeline by stage',
          type: 'bar',
          points: [
            { label: 'Qualification', value: 48200, display: '$48,200' },
            { label: 'Proposal', value: 121500, display: '$121,500' },
            { label: 'Negotiation', value: 76400, display: '$76,400' },
            { label: 'Closed won', value: 33800, display: '$33,800' },
          ],
        },
      },
    },
    {
      key: 'trend',
      label: 'A figure over time',
      summary: 'A line with a dot at each reading and the ends of the run written out beneath it.',
      placement: {
        component: 'caos-chart',
        inputs: {
          label: 'Orders per week',
          type: 'line',
          points: [
            { label: 'Week 1', value: 18 },
            { label: 'Week 2', value: 24 },
            { label: 'Week 3', value: 21 },
            { label: 'Week 4', value: 33 },
            { label: 'Week 5', value: 41 },
            { label: 'Week 6', value: 38 },
          ],
        },
      },
    },
    {
      key: 'inline',
      label: 'A sparkline inside a tile',
      summary: 'The same line stripped to its stroke, sitting in the visual slot StatTile documents but could not fill.',
      placement: {
        component: 'caos-stat-tile',
        inputs: { label: 'Open orders', value: '248', delta: '+12%', trend: 'up' },
        children: [
          {
            component: 'caos-chart',
            slot: 'visual',
            inputs: {
              label: 'Open orders, last six weeks',
              type: 'sparkline',
              points: [18, 24, 21, 33, 41, 38],
            },
          },
        ],
      },
    },
    {
      key: 'nothing',
      label: 'Nothing to draw',
      summary: 'With no readings it says so in words, rather than drawing an empty frame that reads as nought.',
      placement: {
        component: 'caos-chart',
        inputs: { label: 'Revenue by region', type: 'bar', points: [], emptyLabel: 'No revenue recorded yet.' },
      },
    },
  ],
};

export default docs;
